/**
 * v1.3.82 — Cache-backend dispatcher.
 *
 * Reads prefer SQL (fast per-category queries + no structured-clone
 * overhead). If SQL is unavailable or empty for a given key, we
 * transparently fall back to IndexedDB (the existing playlistCache.js
 * implementation) and — when the IDB path returns data — kick a
 * fire-and-forget write to SQL so subsequent reads promote to the
 * faster backend.
 *
 * Writes fan out to BOTH backends during the migration window so a
 * user who ever launches an older v1.3.x build still finds their data
 * in IDB. Once we're confident every device has migrated we can
 * demote IDB to a legacy read-only shim.
 *
 * DO NOT import this module from playlistCache.js directly — it
 * imports playlistCache functions and would create a cycle. Instead,
 * consumers (Browse.jsx, LiveTv.jsx, PlaylistDataContext.jsx) import
 * from here.
 */
import {
  sqlIsAvailable,
  ensureReady as sqlEnsureReady,
  sqlGetChannelsByGroup,
  sqlGetCategoriesForType,
  sqlPutChannels,
  sqlPutEpg,
  sqlPutMeta,
  sqlKvGet,
  sqlKvPut,
  sqlStats,
  sqlGetSeriesTiles,
  sqlGetMovieTiles,
  sqlGetSeriesCategories,
  sqlUpdateSeriesPoster,
  sqlGetSeriesMissingPoster,
} from "./playlistCacheSql";
import {
  getChannelsByGroup as idbGetChannelsByGroup,
  getCategoriesForType as idbGetCategoriesForType,
} from "./playlistCache";

// Kick off SQL init as soon as this module is imported. Non-blocking:
// the very first read after boot may still see `sqlIsAvailable()`
// false and hit IDB.
sqlEnsureReady();

/**
 * Per-category channel loader. Prefers SQL; on SQL miss (either the
 * plugin isn't up or the migration hasn't populated this key yet)
 * we fall back to IDB and opportunistically shadow-write.
 */
export async function getChannelsByGroup(playlistId, contentType, group) {
  if (sqlIsAvailable()) {
    const rows = await sqlGetChannelsByGroup(playlistId, contentType, group);
    if (Array.isArray(rows) && rows.length > 0) return rows;
    // SQL empty → fall through to IDB, then shadow-write.
    const idbRows = await idbGetChannelsByGroup(playlistId, contentType, group);
    if (Array.isArray(idbRows) && idbRows.length > 0) {
      // Fire-and-forget: no need to await the SQL write here.
      sqlPutChannels(playlistId, idbRows).catch(() => {});
    }
    return idbRows;
  }
  return idbGetChannelsByGroup(playlistId, contentType, group);
}

/** Distinct category names + counts. Prefers SQL. */
export async function getCategoriesForType(playlistId, contentType) {
  if (sqlIsAvailable()) {
    const rows = await sqlGetCategoriesForType(playlistId, contentType);
    if (Array.isArray(rows) && rows.length > 0) return rows;
    const idbRows = await idbGetCategoriesForType(playlistId, contentType);
    return idbRows;
  }
  return idbGetCategoriesForType(playlistId, contentType);
}

/**
 * Write-through hook called by playlistCache.js after its normal
 * IDB write finishes. Mirrors the fresh channels + EPG + meta into
 * SQL so subsequent reads promote transparently.
 */
export async function shadowWritePlaylist(playlistId, meta, channels, epgByChannel) {
  if (!sqlIsAvailable()) return { skipped: true };
  const out = await Promise.allSettled([
    sqlPutChannels(playlistId, channels || []),
    sqlPutEpg(playlistId, epgByChannel || {}),
    sqlPutMeta(playlistId, meta || {}),
  ]);
  return {
    channels: out[0].status === "fulfilled" ? out[0].value : 0,
    epg: out[1].status === "fulfilled" ? out[1].value : 0,
    meta: out[2].status === "fulfilled" ? out[2].value : false,
  };
}

/** Best-effort stats snapshot for the Admin → Cache Health card. */
export async function getCacheStats() {
  return sqlStats();
}

// ---------------------------------------------------------------
// v1.3.0 — Pre-aggregated tile reads. These bypass the giant
// `channels` array entirely — the SQL layer has already done the
// GROUP BY / poster resolution at import time.
// ---------------------------------------------------------------

/** Series tiles for one category. Returns `null` when SQL is
 *  unavailable so the caller falls back to the JS aggregation. */
export async function getSeriesTiles(playlistId, group) {
  if (!sqlIsAvailable()) return null;
  return sqlGetSeriesTiles(playlistId, group);
}

/** Movie tiles for one category. Returns `null` when SQL is
 *  unavailable so the caller falls back to the in-memory filter. */
export async function getMovieTiles(playlistId, group) {
  if (!sqlIsAvailable()) return null;
  return sqlGetMovieTiles(playlistId, group);
}

/** Distinct series category names + counts, straight from the
 *  aggregated series_tiles table. */
export async function getSeriesCategories(playlistId) {
  if (!sqlIsAvailable()) return null;
  return sqlGetSeriesCategories(playlistId);
}

/**
 * v1.3.0 — Patch a resolved TMDB poster back onto a series tile.
 * `seriesName` fills every copy of the show across groups.
 */
export async function updateSeriesPoster(playlistId, seriesKey, posterUrl, seriesName) {
  if (!sqlIsAvailable()) return false;
  return sqlUpdateSeriesPoster(playlistId, seriesKey, posterUrl, seriesName);
}

/** List series tiles missing a poster. */
export async function getSeriesMissingPoster(playlistId, limit) {
  if (!sqlIsAvailable()) return null;
  return sqlGetSeriesMissingPoster(playlistId, limit);
}

/** Reflection helper for the debug overlay. */
export { sqlIsAvailable };

// ---------------------------------------------------------------
// v1.3.82 — kv (small-blob) read/write helpers used by the tail-end
// of playlistCache.js (`series_eps`, `vod_cats`, `vod_streams`,
// `live_cats`, `live_streams`, `short_epg`). Each pair mirrors
// exactly one `getCachedXxx` / `cacheXxx` function.
//
// Reads: SQL is consulted only after LRU + IDB miss. Returns `null`
//        outside the caller's TTL window so the caller doesn't need
//        to know SQL exists.
// Writes: SQL is a shadow copy. Fire-and-forget so the primary IDB
//         write path stays hot.
// ---------------------------------------------------------------

/**
 * @param {string} scope    'series_eps' | 'vod_cats' | ...
 * @param {string} key      composite key (e.g. `${playlistId}:${seriesId}`)
 * @param {number} ttlMs    TTL window (matches the caller's IDB TTL)
 * @returns {any|null}      the stored payload, or null on miss / expired
 */
export async function kvFallbackGet(scope, key, ttlMs) {
  if (!sqlIsAvailable()) return null;
  const row = await sqlKvGet(scope, key);
  if (!row) return null;
  if (ttlMs > 0 && Date.now() - row.fetched_at > ttlMs) return null;
  return row.data;
}

/**
 * Shadow-write a kv payload. `data` is stored verbatim; the SQL row
 * records its own fetched_at so the read path can enforce the TTL.
 * Fire-and-forget: the returned promise resolves to `true` on success
 * and `false` when SQL is unavailable or the write failed. Callers
 * SHOULD NOT await this — it's meant to be `.catch(() => {})`-ed.
 */
export async function kvShadowPut(scope, key, data) {
  if (!sqlIsAvailable()) return false;
  return sqlKvPut(scope, key, data);
}
